/**
 * Award Type Templates - System-Level Constants
 * 
 * These define the core logic and behavior of each award type.
 * Organizations customize these templates with their own branding and configuration.
 */

export const AWARD_TYPE_TEMPLATES = {
  ADJUDICATION: {
    typeId: "adjudication",
    defaultName: "Adjudication Awards",
    description: "All routines with adjudication awards in routine order",
    internalLogic: "routineOrderWithAwards",
    supportsVariants: false,
    supportsWinners: false,
    groupingStrategy: "single_list",
    printLayout: "single_table",
    columns: ["#", "Studio", "Routine", "Score", "Age Division", "Category", "Performance Division"],
    showsPlacement: false,
    showsStudioName: false,
    usesPlacementBasedColors: false,
    filters: {
      includeSolos: true,
      includeGroups: true,
      requiresAward: true,
      excludeTypes: ["Performance Only"]
    },
    defaultConfig: {
      sortOrder: "routine_number",
      showScoreInRed: true
    }
  },

  HIGH_SCORE_BY_AGE: {
    typeId: "highScoreByAge",
    defaultName: "High Score by Age",
    description: "Top scoring routines grouped by age division, then category size",
    internalLogic: "groupByAge_thenCategory",
    supportsVariants: true,
    groupingStrategy: "age_then_category",
    printLayout: "grouped_tables_with_headers",
    defaultVariants: [
      {
        id: "10and5",
        name: "Standard (10 & 5)",
        description: "Top 10 solos, Top 5 groups per category",
        soloCount: 10,
        groupCount: 5
      },
      {
        id: "20and10",
        name: "Extended (20 & 10)",
        description: "Top 20 solos, Top 10 groups per category",
        soloCount: 20,
        groupCount: 10
      }
    ],
    columns: ["Placement", "Studio Name", "Routine", "Score", "Age Division", "Category", "Performance Division"],
    showsPlacement: true,
    showsStudioName: true,
    usesPlacementBasedColors: true,
    filters: {
      includeSolos: true,
      includeGroups: true,
      excludeTypes: ["Adjudication Only", "Critique Only", "Performance Only"]
    },
    defaultConfig: {
      ageGroupOrder: ["PeeWee", "Mini", "Junior", "Teen", "Senior", "Open"],
      categoryOrder: ["Solo", "Duo/Trio", "Small Group", "Large Group", "Line", "Ext. Line"],
      excludedCompetitionCategories: [],
      excludedStyleCategories: []
    }
  },

  HIGH_SCORE_BY_DIVISION: {
    typeId: "highScoreByDivision",
    defaultName: "High Score by Performance Division",
    description: "Top scoring routines by performance division",
    internalLogic: "groupByAge_thenDivisionAlphabetical",
    supportsVariants: true,
    groupingStrategy: "age_then_performance_division",
    printLayout: "grouped_tables_with_headers",
    defaultVariants: [
      {
        id: "top3",
        name: "Top 3",
        description: "Top 3 routines per division",
        count: 3
      },
      {
        id: "top5",
        name: "Top 5",
        description: "Top 5 routines per division",
        count: 5
      }
    ],
    columns: ["Placement", "Studio Name", "Routine", "Score", "Age Division", "Category", "Performance Division"],
    showsPlacement: true,
    showsStudioName: true,
    usesPlacementBasedColors: true,
    filters: {
      includeSolos: false,
      includeGroups: true,
      excludeTypes: ["Adjudication Only", "Critique Only", "Performance Only"]
    },
    defaultConfig: {
      ageGroupOrder: ["PeeWee", "Mini", "Junior", "Teen", "Senior", "Open"],
      divisionOrder: "alphabetical",
      excludedCompetitionCategories: [],
      excludedStyleCategories: []
    }
  },

  GREATEST_HITS: {
    typeId: "greatestHits",
    defaultName: "Overall Group Rankings",
    description: "All group routines ranked by score per age division",
    internalLogic: "rankAllGroupsByAgeDescending",
    supportsVariants: false,
    supportsWinners: true,
    groupingStrategy: "age_only",
    printLayout: "grouped_tables_with_headers",
    columns: ["Studio Name", "Routine", "Score", "Age Division", "Category", "Performance Division"],
    showsPlacement: false,
    showsStudioName: true,
    usesPlacementBasedColors: false,
    filters: {
      includeSolos: false,
      includeGroups: true,
      groupMinDancers: 4,
      excludeTypes: ["Adjudication Only", "Critique Only", "Performance Only"]
    },
    defaultConfig: {
      ageGroupOrder: ["PeeWee", "Mini", "Junior", "Teen", "Senior", "Open"],
      allowManualSelection: true,
      // Split age groups: map age group to array of division arrays (for multiple splits)
      // If an age group is not in this map, it shows all divisions
      // Example: { "Teen": [["Small Group"], ["Large Group", "Line"]] } means Teen age group has two splits
      // Each split is an array of divisions that will be shown together
      splitAgeGroups: {}
    }
  },

  STUDIO_STANDOUT: {
    typeId: "studioStandout",
    defaultName: "Studio Rankings",
    description: "Group routines ranked per eligible studio",
    internalLogic: "groupByStudio_rankByScore",
    supportsVariants: false,
    supportsWinners: true,
    requiresEligibilityCheck: true,
    groupingStrategy: "single_list",
    printLayout: "single_table_with_header",
    columns: ["Studio Name", "Routine", "Score", "Age Division", "Category", "Performance Division"],
    showsPlacement: false,
    showsStudioName: true,
    usesPlacementBasedColors: false,
    filters: {
      includeSolos: false,
      includeGroups: true,
      groupMinDancers: 4,
      showUnscored: true, // Unique to Studio Standout
      excludeTypes: ["Adjudication Only", "Critique Only", "Performance Only"]
    },
    defaultConfig: {
      sortOrder: "alphabetical",
      allowManualSelection: true,
      eligibilityCriteria: {
        minTotalRoutines: 15,
        minGroupRoutines: 7
      }
    }
  }
};

/**
 * Helper to get template by typeId
 */
export const getTemplateById = (typeId) => {
  return Object.values(AWARD_TYPE_TEMPLATES).find(template => template.typeId === typeId);
};

/**
 * Helper to get all available template types
 */
export const getAllTemplateTypes = () => {
  return Object.values(AWARD_TYPE_TEMPLATES);
};

/**
 * Default custom awards configuration structure
 */
export const DEFAULT_CUSTOM_AWARD = {
  templateType: null,
  enabled: true,
  displayName: "",
  config: {}
};

/**
 * Helper to create a default custom award from a template
 */
export const createCustomAwardFromTemplate = (templateType) => {
  const template = getTemplateById(templateType);
  if (!template) return null;

  return {
    templateType: template.typeId,
    enabled: true,
    displayName: template.defaultName,
    config: {
      ...template.defaultConfig,
      // Initialize division exclusions as empty array
      excludedDivisions: [],
      // Initialize competition category exclusions as empty array
      excludedCompetitionCategories: template.defaultConfig.excludedCompetitionCategories ?? [],
      // Initialize style category exclusions as empty array
      excludedStyleCategories: template.defaultConfig.excludedStyleCategories ?? [],
      // Initialize split age groups for Greatest Hits
      // Empty object means all age groups show all divisions
      splitAgeGroups: template.defaultConfig.splitAgeGroups ?? {},
      ...(template.supportsVariants ? {
        activeVariants: template.defaultVariants.map(v => v.id),
        defaultVariant: template.defaultVariants[0].id,
        variantNames: {}
      } : {}),
      ...(template.supportsWinners ? {
        enableWinnersVariant: true,
        winnersDisplayName: `${template.defaultName} - Winners`
      } : {})
    }
  };
};

